import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  getStoredWorkspace,
  persistWorkspace,
  resolveWorkspace,
} from './workspaceScope';
import { buildApiUrl, getApiHeaders } from '../config';
import { getAuthToken } from '../auth/authUtils';

const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ initialWorkspace, children }) {
  const [workspace, setWorkspaceState] = useState(() =>
    resolveWorkspace(initialWorkspace || getStoredWorkspace())
  );
  const [scopeOptions, setScopeOptions] = useState([]);
  const [scopeSource, setScopeSource] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    async function loadBackendScopes() {
      try {
        const response = await fetch(buildApiUrl('/api/bot/ui/orchestration/symbol-universe/scopes'), {
          headers: getApiHeaders({ Authorization: `Bearer ${getAuthToken()}` }),
          cache: 'no-store',
        });
        if (!response.ok) throw new Error(`Scope API returned ${response.status}`);
        const rows = await response.json();
        if (!Array.isArray(rows)) throw new Error('Scope API returned an invalid response');
        if (cancelled) return;
        setScopeOptions(rows);
        setScopeSource('backend');
        if (rows.length === 0) {
          setWorkspaceState(resolveWorkspace());
          return;
        }
        setWorkspaceState(current => {
          const stillActive = rows.some(row =>
            row.exchange === current.exchange && row.marketType === current.marketType);
          const resolved = stillActive ? current : resolveWorkspace(rows[0]);
          persistWorkspace(resolved);
          return resolved;
        });
      } catch (error) {
        if (!cancelled) setScopeSource('local-storage-fallback');
      }
    }
    loadBackendScopes();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const resolvedInitialWorkspace = resolveWorkspace(initialWorkspace || getStoredWorkspace());
    setWorkspaceState((prevWorkspace) => {
      if (
        prevWorkspace.exchange === resolvedInitialWorkspace.exchange &&
        prevWorkspace.marketType === resolvedInitialWorkspace.marketType
      ) {
        return prevWorkspace;
      }

      return resolvedInitialWorkspace;
    });
  }, [initialWorkspace]);

  useEffect(() => {
    if (workspace?.exchange && workspace?.marketType) {
      console.debug("WorkspaceContext active workspace", workspace);
    }
  }, [workspace]);

  const updateWorkspace = useCallback((nextWorkspace) => {
    const resolved = persistWorkspace({
      ...workspace,
      ...nextWorkspace,
    });
    setWorkspaceState((prevWorkspace) => {
      if (
        prevWorkspace.exchange === resolved.exchange &&
        prevWorkspace.marketType === resolved.marketType
      ) {
        return prevWorkspace;
      }

      return resolved;
    });
    return resolved;
  }, [workspace]);

  const value = useMemo(
    () => ({
      workspace,
      exchange: workspace.exchange,
      marketType: workspace.marketType,
      scopeOptions,
      scopeSource,
      setWorkspace: updateWorkspace,
    }),
    [workspace, scopeOptions, scopeSource, updateWorkspace]
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}
